import { forwardRef, useLayoutEffect, useRef } from 'react'

import { useTimeout } from 'slab/hooks/useTimeout'
import { useValueSetter } from 'slab/hooks/useValueSetter'
import { mergeRefs } from 'utils/ref_utils'

interface ProxyDateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  date: string | null;
  onValueChange: (value: string | number) => void
}

export const ProxyDateInput = forwardRef<HTMLInputElement, ProxyDateInputProps>(({
  date,
  onValueChange,
  ...props
}, forwardedRef) => {
  const timeout = useTimeout()
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * By default, `react-hook-form` uses uncontrolled inputs, but it manages
   * input values directly via the DOM instead of React. Usually in React, an
   * uncontrolled input's initial value is set via the `defaultValue` prop, but
   * `react-hook-form` uses the DOM to set the initial value directly as soon as
   * the input has a `ref` attached. To detect this we're using a layout effect
   * to check the input's value immediately after mounting and posting the
   * change to the owner component if it differs from the value that component
   * was initialized with.
   */
  useLayoutEffect(() => {
    const value = inputRef.current?.value

    if (value && value !== date) {
      onValueChange(value)
    }
  }, [])

  /**
   * Detects changes made to the input value directly via the DOM (for example,
   * by `react-hook-form`) and posts the change to the owner component.
   */
  useValueSetter(inputRef, (value) => {
    onValueChange(value)
  })

  return (
    <input
      {...props}
      aria-hidden
      className='field opacity-0 absolute top-0 left-0 bottom-0 right-0 -z-[1]'
      defaultValue={date || ''}
      onClick={(event) => {
        // We want to prevent the native datepicker dialog from opening
        // on mobile devices if the user taps the input's label. Calling
        // `event.preventDefault()` on focus and click handlers seems to
        // be sufficient for Android, but iOS doesn't seem to respect
        // that. So we're also temporarily changing the input type from
        // `date` to `text`, waiting for iOS's `onClick` delay of 350ms,
        // and then resetting it back, which seems to do the trick.
        const { currentTarget } = event
        currentTarget.type = 'text'
        timeout.set(() => {
          currentTarget.type = 'date'
        }, 350)

        event.preventDefault()
        event.stopPropagation()
      }}
      tabIndex={-1}
      ref={mergeRefs(inputRef, forwardedRef)}
      type='date'
    />
  )
})
